import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHero, StatusPill } from "@/components/shared/Visual";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
  ArrowLeft, ShieldCheck, Users2, FileText, MessageSquare, Send,
  UserPlus, Trash2, PlayCircle, Lock, Unlock, X, ChevronRight, ClipboardList,
  Power, PhoneOff,
} from "lucide-react";

const STATUSES = [
  "awaiting_ops_approval", "accepted", "team_being_built", "staff_awaiting_confirmation",
  "deployment_confirmed", "staff_en_route", "live", "completed", "closed", "declined",
];
const LABEL = {
  awaiting_ops_approval: "Awaiting Approval", accepted: "Accepted", team_being_built: "Team Being Built",
  staff_awaiting_confirmation: "Staff Awaiting Confirmation", deployment_confirmed: "Deployment Confirmed",
  staff_en_route: "Staff En Route", live: "Live", completed: "Completed", closed: "Closed", declined: "Declined",
};

export default function AdminMdrDetail() {
  const { id } = useParams();
  const [dep, setDep] = useState(null);
  const [pros, setPros] = useState([]);
  const [msg, setMsg] = useState("");
  const [sop, setSop] = useState({ title: "", url: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeSummary, setCloseSummary] = useState("");
  const [addStaffOpen, setAddStaffOpen] = useState(false);
  const [staffQ, setStaffQ] = useState("");

  const load = () => api.get(`/managed-deployments/${id}`).then((r) => setDep(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); api.get("/company/pros-picker").then((r) => setPros(r.data || [])).catch(() => {}); }, [id]);

  if (!dep) return <div className="p-8 txt-lo text-center">Loading…</div>;
  const readOnly = dep.status === "closed" || dep.status === "declined";

  const setStatus = async (status) => {
    setBusy(true);
    try { await api.patch(`/admin/managed-deployments/${id}/status`, { status }); toast.success("Status updated"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const toggleAccess = async () => {
    setBusy(true);
    try { await api.patch(`/admin/managed-deployments/${id}/access`, { enabled: !dep.access_enabled }); toast.success(dep.access_enabled ? "Access revoked" : "Access granted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const addStaff = async (u, isLead) => {
    setBusy(true);
    try { await api.post(`/admin/managed-deployments/${id}/staff`, { user_id: u.id, role: "guard", is_team_lead: !!isLead }); toast.success("Staff added"); setAddStaffOpen(false); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const removeStaff = async (uid) => {
    setBusy(true);
    try { await api.delete(`/admin/managed-deployments/${id}/staff/${uid}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const addSop = async () => {
    if (!sop.title.trim()) return;
    setBusy(true);
    try { await api.post(`/admin/managed-deployments/${id}/sop`, sop); toast.success("SOP added"); setSop({ title: "", url: "", notes: "" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const sendMsg = async () => {
    if (!msg.trim()) return;
    setBusy(true);
    try { await api.post(`/managed-deployments/${id}/message`, { body: msg.trim() }); toast.success("Sent"); setMsg(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const closeDeployment = async () => {
    setBusy(true);
    try { await api.post(`/admin/managed-deployments/${id}/close`, { summary: closeSummary }); toast.success("Deployment closed"); setCloseOpen(false); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-mdr-detail-page">
      <PageHero
        eyebrow="Managed Deployment"
        title={dep.job_title}
        sub={<span>{dep.company_name} · {dep.location} · {dep.date}</span>}
        right={
          <div className="flex items-center gap-2">
            <StatusPill kind={dep.status === "live" ? "live" : dep.status === "closed" ? "neutral" : "active"}>{LABEL[dep.status]}</StatusPill>
            <StatusPill kind={dep.access_enabled ? "active" : "neutral"}>{dep.access_enabled ? "Access On" : "Access Off"}</StatusPill>
          </div>
        }
      >
        <Link to="/admin/managed-deployments" className="text-xs text-brand font-outfit font-bold uppercase tracking-widest flex items-center gap-1 hover:underline">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to queue
        </Link>
      </PageHero>

      {/* Ops admin controls */}
      <div className="card-dark p-5">
        <div className="eyebrow eyebrow-brand mb-3 flex items-center gap-2"><ClipboardList className="w-3.5 h-3.5" /> Operations Controls</div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={dep.status} onValueChange={setStatus} disabled={busy || readOnly}>
            <SelectTrigger className="w-56" data-testid="mdr-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{LABEL[s]}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={toggleAccess} disabled={busy || readOnly} variant="outline" className="btn-ghost-dark" data-testid="mdr-access-toggle">
            {dep.access_enabled ? <><PhoneOff className="w-4 h-4 mr-1" /> Revoke Access</> : <><Power className="w-4 h-4 mr-1" /> Enable Access</>}
          </Button>
          {!readOnly && (
            <Button onClick={() => setCloseOpen(true)} disabled={busy} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="mdr-close-btn">
              <X className="w-4 h-4 mr-1" /> Close Deployment
            </Button>
          )}
          <div className="ml-auto eyebrow">Ops admin: <span className="txt-hi font-outfit font-semibold ml-1">{dep.assigned_admin_name || "—"}</span></div>
        </div>
        {readOnly && <div className="mt-3 alert-card info"><Lock className="w-4 h-4 text-brand mt-0.5" /><div className="txt-md text-xs font-manrope flex-1">Closed — read-only. All records preserved.</div></div>}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Team */}
        <div className="card-dark p-5" data-testid="mdr-team-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="eyebrow eyebrow-brand flex items-center gap-2"><Users2 className="w-3.5 h-3.5" /> Team ({(dep.team_members || []).length})</div>
            {!readOnly && (
              <Dialog open={addStaffOpen} onOpenChange={setAddStaffOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="btn-ghost-dark" data-testid="mdr-add-staff-btn"><UserPlus className="w-3.5 h-3.5 mr-1" /> Add</Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader><DialogTitle>Assign staff to deployment</DialogTitle></DialogHeader>
                  <Input placeholder="Search verified pros…" value={staffQ} onChange={(e) => setStaffQ(e.target.value)} />
                  <div className="space-y-2 max-h-96 overflow-y-auto mt-2">
                    {pros.filter((p) => !staffQ || (p.full_name || "").toLowerCase().includes(staffQ.toLowerCase()) || (p.secusource_id || "").toLowerCase().includes(staffQ.toLowerCase())).map((p) => (
                      <div key={p.id} className="flex items-center gap-2 p-2 rounded border">
                        <div className="min-w-0 flex-1">
                          <div className="font-outfit font-semibold text-white text-sm truncate">{p.full_name} <span className="text-xs font-mono text-white/60 ml-1">{p.secusource_id}</span></div>
                          <div className="text-xs text-white/60">{p.primary_role} · {p.rating ? `${Number(p.rating).toFixed(1)}★` : "—"}</div>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => addStaff(p, false)}>Add</Button>
                        <Button size="sm" onClick={() => addStaff(p, true)} className="bg-brand hover:bg-brand-hover text-white">+ Lead</Button>
                      </div>
                    ))}
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
          {(dep.team_members || []).length === 0 ? (
            <div className="txt-lo text-sm font-manrope">No staff assigned yet.</div>
          ) : (
            <div className="space-y-2">
              {dep.team_members.map((m) => (
                <div key={m.user_id} className="card-inner p-3 flex items-center gap-3" data-testid={`mdr-team-member-${m.user_id}`}>
                  <div className="w-8 h-8 rounded-full bg-brand/15 text-brand flex items-center justify-center"><Users2 className="w-4 h-4" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="font-outfit font-semibold txt-hi text-sm truncate">
                      {m.name} <span className="text-xs font-mono txt-lo ml-1">{m.secusource_id}</span>
                    </div>
                    <div className="eyebrow mt-0.5">{m.is_team_lead ? "Team Lead" : (m.role || "guard")} · {m.status}</div>
                  </div>
                  {!readOnly && (
                    <button onClick={() => removeStaff(m.user_id)} className="txt-lo hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* SOPs */}
        <div className="card-dark p-5" data-testid="mdr-sops-panel">
          <div className="eyebrow eyebrow-brand flex items-center gap-2 mb-3"><FileText className="w-3.5 h-3.5" /> SOPs &amp; Documents</div>
          {!readOnly && (
            <div className="grid gap-2 mb-3">
              <Input placeholder="Title" value={sop.title} onChange={(e) => setSop({ ...sop, title: e.target.value })} data-testid="mdr-sop-title" />
              <Input placeholder="URL (optional)" value={sop.url} onChange={(e) => setSop({ ...sop, url: e.target.value })} />
              <Textarea rows={2} placeholder="Notes" value={sop.notes} onChange={(e) => setSop({ ...sop, notes: e.target.value })} />
              <Button onClick={addSop} disabled={busy || !sop.title.trim()} className="btn-brand" data-testid="mdr-sop-add-btn">Add SOP</Button>
            </div>
          )}
          {(dep.sops || []).map((s) => (
            <div key={s.id} className="card-inner p-3 mb-2">
              <div className="font-outfit font-semibold txt-hi text-sm">{s.title}</div>
              {s.notes && <div className="text-xs txt-lo font-manrope mt-1">{s.notes}</div>}
              {s.url && <a href={s.url} target="_blank" rel="noreferrer" className="text-brand text-xs font-outfit font-semibold hover:underline mt-1 inline-block">Open →</a>}
            </div>
          ))}
        </div>
      </div>

      {/* Messages */}
      <div className="card-dark p-5" data-testid="mdr-messages-panel">
        <div className="eyebrow eyebrow-brand flex items-center gap-2 mb-3"><MessageSquare className="w-3.5 h-3.5" /> Company Updates</div>
        <div className="max-h-72 overflow-y-auto space-y-2 mb-3">
          {(dep.messages || []).length === 0 ? <div className="txt-lo text-sm font-manrope">No messages yet.</div> : dep.messages.map((m) => (
            <div key={m.id} className={`p-2 rounded ${m.author_role === "super_admin" ? "bg-brand/15 border border-brand/30" : "bg-white/5 border border-white/10"}`}>
              <div className="eyebrow mb-1">{m.author_name} · {new Date(m.created_at).toLocaleString()}</div>
              <div className="text-sm txt-hi font-manrope">{m.body}</div>
            </div>
          ))}
        </div>
        {!readOnly && (
          <div className="flex gap-2">
            <Input placeholder="Message to company…" value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendMsg()} data-testid="mdr-msg-input" />
            <Button onClick={sendMsg} disabled={busy || !msg.trim()} className="btn-brand" data-testid="mdr-msg-send"><Send className="w-4 h-4 mr-1" /> Send</Button>
          </div>
        )}
      </div>

      {/* Incidents */}
      {(dep.incidents || []).length > 0 && (
        <div className="card-dark p-5" data-testid="mdr-incidents-panel">
          <div className="eyebrow eyebrow-brand mb-3">Incidents ({dep.incidents.length})</div>
          <div className="space-y-2">
            {dep.incidents.map((i) => (
              <div key={i.id} className="alert-card">
                <div className="flex-1">
                  <div className="font-outfit font-semibold txt-hi text-sm">{i.report?.what_happened}</div>
                  <div className="eyebrow mt-1">{i.raised_by_name} · {new Date(i.created_at).toLocaleString()}{i.escalated && " · ESCALATED"}</div>
                  {i.report?.action_required && <div className="text-xs text-rose-200/80 font-manrope mt-1">Action: {i.report.action_required}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Audit log */}
      <div className="card-dark p-5" data-testid="mdr-audit">
        <div className="eyebrow eyebrow-brand mb-3">Audit Log</div>
        <div className="space-y-1 text-xs font-manrope max-h-64 overflow-y-auto">
          {(dep.audit_log || []).slice().reverse().map((a, i) => (
            <div key={i} className="flex items-center gap-2 py-1 border-b border-white/5">
              <span className="txt-lo w-40 flex-shrink-0">{new Date(a.at).toLocaleString()}</span>
              <span className="txt-md font-outfit font-semibold w-56 flex-shrink-0">{a.action}</span>
              <span className="txt-lo truncate">{a.actor_name} ({a.actor_role})</span>
            </div>
          ))}
        </div>
      </div>

      {/* Close dialog */}
      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Close Deployment</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div className="text-sm text-white/70 font-manrope">Access will be revoked. Company retains a read-only final record.</div>
            <Label>Closing summary</Label>
            <Textarea rows={4} value={closeSummary} onChange={(e) => setCloseSummary(e.target.value)} placeholder="Deployment completed without incident. Final record archived." />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseOpen(false)}>Cancel</Button>
            <Button onClick={closeDeployment} disabled={busy} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="mdr-close-confirm">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
